/**
 * Contagem Item Service
 *
 * Handles item-level operations: manual add/delete/update,
 * UI helpers (faixa aging labels/colors, format conversion).
 */
import apiClient from '../apiClient';
import type {
  ItemContagemImportado,
  ItemContagemListResponse,
  ItemManualInput,
  AddItemResponse,
  DeleteItemResponse,
  UpdateItemResponse,
} from './contagemTypes';

class ContagemItemService {
  /**
   * List expected inventory items (paginated)
   * GET /api/contagens/{job_id}/itens-esperados
   */
  async getItensEsperados(
    jobId: number,
    page: number = 1,
    pageSize: number = 100
  ): Promise<ItemContagemListResponse> {
    const response = await apiClient.get(`/contagens/${jobId}/itens-esperados`, {
      params: { page, page_size: pageSize },
    });
    return response.data;
  }

  /**
   * Get all expected items (all pages)
   * Helper method to fetch all items at once
   */
  async getAllItensEsperados(jobId: number): Promise<ItemContagemImportado[]> {
    const items: ItemContagemImportado[] = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await this.getItensEsperados(jobId, page, 100);
      items.push(...response.results);

      if (response.results.length < 100) {
        hasMore = false;
      } else {
        page++;
      }
    }

    return items;
  }

  /**
   * Add a manual item to the counting
   * POST /api/jobs/{job_id}/itens
   */
  async addItemManual(jobId: number, data: ItemManualInput): Promise<AddItemResponse> {
    try {
      const response = await apiClient.post(`/jobs/${jobId}/itens`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao adicionar item');
    }
  }

  /**
   * Delete an item from the counting
   * DELETE /api/jobs/{job_id}/itens/{item_id}
   */
  async deleteItem(jobId: number, itemId: number): Promise<DeleteItemResponse> {
    try {
      const response = await apiClient.delete(`/jobs/${jobId}/itens/${itemId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao excluir item');
    }
  }

  /**
   * Update item fields (Sistema, Validade, Contado)
   * PATCH /api/contagens/{job_id}/itens/{item_id}
   */
  async updateItemContagem(
    jobId: number,
    itemId: number,
    data: {
      quantidade_sistema?: number;
      data_validade?: string; // Format: YYYY-MM-DD
      quantidade_contada?: number;
      quantidade_segregada?: number;
    }
  ): Promise<UpdateItemResponse> {
    try {
      const response = await apiClient.patch(`/contagens/${jobId}/itens/${itemId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Erro ao atualizar item');
    }
  }

  // ============================================================
  // UI HELPERS
  // ============================================================

  /**
   * Calculate aging band label based on faixa_aging value
   */
  getFaixaAgingLabel(faixaAging: string): string {
    const labels: Record<string, string> = {
      '1': '1: Expirado',
      '2': '2: 0 a 30 dias',
      '3': '3: 31 a 60 dias',
      '4': '4: 61 a 90 dias',
      '5': '5: 91 a 180 dias',
      '6': '6: maior que 180 dias',
    };
    return labels[faixaAging] || faixaAging;
  }

  /**
   * Get aging band CSS color classes
   */
  getFaixaAgingColor(faixaAging: string): string {
    if (faixaAging === '1') {
      return 'bg-red-100 text-red-700';    // Expirado
    }
    const num = parseInt(faixaAging);
    if (num <= 3) {
      return 'bg-yellow-100 text-yellow-700'; // 0-60 dias
    }
    return 'bg-green-100 text-green-700';    // 61+ dias
  }

  /**
   * Helper: Convert ItemContagemImportado to UI format
   */
  convertToUIFormat(item: ItemContagemImportado) {
    // N176: `|| 0` nas quantidades. `sistema` alimenta `diff`, `sobras` e
    // `faltas` — um NaN aqui contamina a linha inteira, e foi exatamente essa
    // classe de erro que chegou na tela da Alana (contagem 1758).
    const sistema = parseFloat(item.quantidade_sistema) || 0;
    // Epic 48: Use quantidade_contada from Conferência Lacres (if available)
    //
    // ⛔ N171: "nunca contado" e "contado zero" são estados DIFERENTES e este
    // era o ponto onde a diferença morria. O backend manda `null` corretamente
    // (api/jobs_excel/download.py), e o antigo `|| '0'` colapsava os dois na
    // mesma célula `0` — sem badge, sem cor, sem tooltip. Foi por isso que um
    // item que nem existe no hospital "parecia contado" na contagem 1800.
    //
    // `nuncaContado` deixa a tela mostrar `—` (não conferido) em vez de `0`
    // (conferido e não achado). Os cálculos derivados continuam tratando
    // ausência como zero, que é o comportamento correto para falta.
    // ⛔ N175: quem decide o "—" é o BACKEND (`nunca_contado`), porque a regra
    // depende do status da contagem: em andamento mostra "—", encerrada mostra
    // 0 porque item sem bipe é falta. O fallback pela checagem de null cobre
    // backend e frontend fora de sincronia — mesmo padrão do
    // `quantidade_a_segregar`.
    //
    // ⛔ N176 — DUAS PERGUNTAS DIFERENTES, NAO MISTURAR:
    //   (a) QUANTO vale a celula      -> sempre do valor CRU, ausencia = 0
    //   (b) MOSTRA "—" ou o numero?   -> `nuncaContado`, que depende do STATUS
    // Ate aqui `contado` era calculado a partir de (b), e o N175 quebrou isso:
    // numa contagem ENCERRADA o backend manda `nunca_contado: false` (porque
    // deve mostrar 0) com `quantidade_contada: null` (o dado cru, intocado) —
    // e `parseFloat(null)` e **NaN**. Foi o que a Alana viu na contagem 1758.
    const semValor = item.quantidade_contada === null
      || item.quantidade_contada === undefined
      || item.quantidade_contada === '';
    // (a) o numero: ausencia e ZERO, e nunca NaN.
    const contado = semValor ? 0 : (parseFloat(item.quantidade_contada as string) || 0);
    // (b) so a exibicao. Quem decide e o BACKEND (`nunca_contado`), porque a
    // regra depende do status: em andamento mostra "—", encerrada mostra 0
    // porque item sem bipe e falta. O fallback cobre backend e frontend fora de
    // sincronia — mesmo padrao do `quantidade_a_segregar`.
    const nuncaContado = item.nunca_contado ?? semValor;
    // Epic 44. ⛔ N172: a coluna "Segregar" mostra `quantidade_a_segregar`, que
    // o backend já calcula com a regra única (não se segrega o que não existe;
    // contagem encerrada sem bipe é falta). `quantidade_coletar` continua
    // vindo cru porque é o PEDIDO do cliente — é ele que desce para o aparelho
    // e faz o beep, e limitá-lo apagaria o pedido. Fallback para o campo antigo
    // enquanto backend e frontend não estiverem na mesma versão.
    const coletar = parseFloat(
      item.quantidade_a_segregar ?? item.quantidade_coletar ?? '0'
    ) || 0;  // N176: nunca NaN
    const diff = contado - sistema;
    const sobras = diff > 0 ? diff : 0;
    const faltasValue = diff < 0 ? Math.abs(diff) : 0;

    return {
      id: item.id,
      cliente: item.cliente_nome_fantasia || '-',
      regime: item.regime || '-',
      grupo: 'Produtos',
      produto: item.codigo_produto,
      descricao: item.descricao,
      lote: item.lote,
      // Format date without Date object to avoid timezone issues (UTC vs Brazil UTC-3)
      validade: item.data_validade
        ? (item.data_validade.includes('-')
            ? item.data_validade.split('-').reverse().join('/')  // "2026-12-02" → "02/12/2026"
            : item.data_validade)
        : '',
      faixaAging: this.getFaixaAgingLabel(item.faixa_aging),
      sistema,
      contado,
      // N171: `true` = item nunca foi bipado nem editado. A tela mostra `—`
      // em vez de `0`, para a base não ler falta como conferência.
      nuncaContado,
      coletar,  // Epic 44
      sobras,
      faltas: faltasValue,
      segregar: '',
      // Epic 42: Cirurgia matching fields (populated by useCirurgiaMatching)
      faturar: 0,
      pacientes: '',
      // EPIC-SEG: quantidade_segregada from mobile sync (primary source for "Coletado")
      // Falls back to useConferenciaLacresMatching if not available
      quantidade_segregada: item.quantidade_segregada ? parseFloat(item.quantidade_segregada) : null,
      segregado: item.quantidade_segregada ? parseFloat(item.quantidade_segregada) : 0,
      coletado: item.quantidade_coletada ? parseFloat(item.quantidade_coletada) : 0,
      // Epic 49: Níveis de vencimento do cliente + dias para vencer
      diasParaVencer: item.dias_para_vencer,
      sinalizar_vencimento_1: item.sinalizar_vencimento_1 ?? 90,
      sinalizar_vencimento_2: item.sinalizar_vencimento_2 ?? 120,
      sinalizar_vencimento_3: item.sinalizar_vencimento_3 ?? 180,
    };
  }
}

export default new ContagemItemService();
